import React, {
    useCallback,
    useEffect,
    useMemo,
    useState
} from "react";

import {
    Card,
    Typography
} from "antd";

import {
    useDispatch,
    useSelector
} from "react-redux";

import ConversationNotificationService
    from "../../../../servics/notification/conversation/conversationNotificationService";

import ConversationOfferService
    from "../../../../servics/notification/conversation/ConversationOfferService";

import TransactionService
    from "../../../../servics/notification/transaction/TransactionService";

import NotificationStats
    from "./NotificationStats";

import NotificationFilter
    from "./NotificationFilters";

import NotificationList
    from "./NotificationList";

import ConversationDrawer
    from "./drawer/ConversationDrawer";

import FinalizeProductPurchaseDrawer
    from "./drawer/FinalizeProductPurchaseDrawer";

import AgreementList
    from "./agreement/AgreementList";

import OfferList
    from "./offer/OfferList";

import ArchivedNotificationList
    from "./ArchivedNotificationList";

import DeliveryList
    from "./delivery/DeliveryList";

import NegotiationList
    from "./negotiation/NegotiationList";

import PaymentList
    from "./payment/PaymentList";

import {
    loadNotificationsStart,
    loadNotificationsSuccess,
    loadNotificationsFailure,

    loadConversationStart,
    loadConversationSuccess,
    loadConversationFailure,

    markNotificationAsRead,

    openDrawer as openConversationDrawer,
    closeDrawer as closeConversationDrawer
} from "../../../../store/reducers/notification/conversation/conversationNotificationSlice";

import {
    openDrawer as openTransactionDrawer,
    closeDrawer as closeTransactionDrawer,

    loadAgreementStart,
    loadAgreementSuccess,
    loadAgreementFailure,

    clearTransaction,
    loadAgreementsSuccess
} from "../../../../store/reducers/notification/transaction/transactionSlice";
import PaymentFinalizationDrawer from "./drawer/PaymentFinalizationDrawer";



const {
    Title
} = Typography;


/*
 * ================================================================
 * CONSTANTS
 * ================================================================
 */

const NOTIFICATION_POLLING_INTERVAL =
    10 * 1000;


/*
 * ================================================================
 * NOTIFICATION HELPERS
 * ================================================================
 */

/**
 * Vérifie si la notification correspond
 * à une notification de conversation Marketplace.
 */
const isConversationNotification = (
    notification
) => {

    return (

        notification?.notificationType ===
        "CONVERSATION"

        &&

        (
            notification?.referenceType ===
            "INQUIRY"

            ||

            notification?.referenceType ===
            "CONVERSATION"
        )

        &&

        Boolean(
            notification?.referenceId
        )

    );

};


/**
 * Normalise une valeur d'enum.
 */
/**
 * Fusionne l'avancement post-Agreement (GET /marketplace/purchases) dans les
 * accords : inquiryId, checkout, dernier paiement, commande, livraison.
 */
const withPurchaseProgress = (agreements = [], purchases = []) => {
    const byAgreement = new Map((purchases || []).map(purchase => [purchase.agreementId, purchase]));
    return (agreements || []).map(agreement => {
        const purchase = byAgreement.get(agreement.id);
        if (!purchase) {
            return agreement;
        }
        return {
            ...agreement,
            inquiryId: purchase.inquiryId,
            productAmount: purchase.productAmount,
            totalAmount: purchase.totalAmount,
            checkoutStatus: purchase.checkoutStatus,
            checkoutDeliveryType: purchase.deliveryType,
            checkoutPaymentMethod: purchase.paymentMethod,
            latestPaymentStatus: purchase.latestPaymentStatus,
            orderId: purchase.orderId,
            orderNumber: purchase.orderNumber,
            deliveryStatus: purchase.deliveryStatus
        };
    });
};

/** Accord ayant un checkout (choix de livraison et de paiement enregistrés). */
const hasCheckout = agreement => Boolean(agreement?.checkoutStatus);

const normalizeEnumValue = (
    value
) => {

    return String(
        value || ""
    )
        .trim()
        .toUpperCase();

};



/*
 * ================================================================
 * COMPONENT
 * ================================================================
 */

const Notification = () => {

    const dispatch =
        useDispatch();

    /*
     * Accord dont le paiement est en cours de finalisation
     * (après validation des choix, "Payer maintenant", ou retour de checkout).
     */
    const [
        paymentAgreement,
        setPaymentAgreement
    ] = useState(null);

    /*
     * ============================================================
     * CURRENT USER
     * ============================================================
     */

    const tenantUser =
        useSelector(
            state =>
                state.tenantUser?.tenantUser
        );


    const currentUserId =
        tenantUser?.id
            ? String(
                tenantUser.id
            )
            : null;


    /*
     * ============================================================
     * CONVERSATION STORE
     * ============================================================
     */

    const {
        loading,
        notifications,
        unreadCount,
        drawerOpen
    } = useSelector(
        state =>
            state.conversationNotification
    );


    /*
     * ============================================================
     * TRANSACTION STORE
     * ============================================================
     */

    const {
        drawerOpen:
            transactionDrawerOpen,

        selectedAgreement,

        agreements = []
    } = useSelector(
        state =>
            state.transaction
    );


    /*
     * ============================================================
     * FILTER
     * ============================================================
     */

    const [
        filter,
        setFilter
    ] = useState(
        "ALL"
    );


    /*
     * ============================================================
     * LOAD AGREEMENTS
     * ============================================================
     */

    const loadAgreements =
        useCallback(
            async () => {

                try {

                    dispatch(
                        loadAgreementStart()
                    );


                    const [data, purchases] =
                        await Promise.all([
                            TransactionService.getBuyerAgreements(),
                            // Avancement post-Agreement (inquiryId, checkout, paiement, commande) :
                            // l'Agreement lui-même n'est jamais modifié par le paiement.
                            TransactionService.getPurchases().catch(purchaseError => {
                                console.error("Achats indisponibles", purchaseError);
                                return [];
                            })
                        ]);


                    dispatch(
                        loadAgreementsSuccess(
                            withPurchaseProgress(data, purchases)
                        )
                    );

                }
                catch (error) {

                    console.error(
                        "Unable to load agreements",
                        error
                    );


                    dispatch(
                        loadAgreementFailure(
                            error?.response?.data?.message ||
                            error?.message ||
                            "Impossible de charger les accords"
                        )
                    );

                }

            },
            [
                dispatch
            ]
        );


    /*
     * ============================================================
     * LOAD NOTIFICATIONS
     * ============================================================
     */

    const loadNotifications =
        useCallback(
            async () => {

                try {

                    dispatch(
                        loadNotificationsStart()
                    );


                    const response =
                        await ConversationNotificationService
                            .getNotifications();


                    dispatch(
                        loadNotificationsSuccess(
                            response
                        )
                    );

                }
                catch (error) {

                    console.error(
                        "Unable to load notifications",
                        error
                    );


                    dispatch(
                        loadNotificationsFailure(
                            error?.response?.data?.message ||
                            error?.message ||
                            "Impossible de charger les notifications"
                        )
                    );

                }

            },
            [
                dispatch
            ]
        );


    /*
     * ============================================================
     * INITIAL LOAD + POLLING
     * ============================================================
     */

    useEffect(
        () => {

            loadNotifications();

            loadAgreements();


            const timer =
                setInterval(
                    () => {

                        loadNotifications();

                        loadAgreements();

                    },
                    NOTIFICATION_POLLING_INTERVAL
                );


            return () =>
                clearInterval(
                    timer
                );

        },
        [
            loadNotifications,
            loadAgreements
        ]
    );


    /*
     * ============================================================
     * OPEN CONVERSATION
     *
     * ROUTING :
     *
     * ACCEPTED
     *     ↓
     * AGREEMENT
     *     ↓
     * FINALIZE DRAWER
     *
     * sinon
     *     ↓
     * CONVERSATION DRAWER
     * ============================================================
     */

    const openConversation =
        useCallback(
            async notification => {

                console.log(
                    "========== MARKETPLACE NOTIFICATION CLICK =========="
                );

                console.log(
                    "notification:",
                    notification
                );


                const inquiryId =
                    notification?.referenceId;


                if (!inquiryId) {

                    console.error(
                        "Impossible d'ouvrir la notification : inquiryId absent",
                        notification
                    );

                    return;

                }


                try {

                    /*
                     * =================================================
                     * 1. MARK AS READ
                     * =================================================
                     */

                    if (
                        notification?.id &&
                        !notification?.read
                    ) {

                        try {

                            await ConversationNotificationService
                                .markNotificationAsRead(
                                    notification.id
                                );


                            dispatch(
                                markNotificationAsRead(
                                    notification.id
                                )
                            );

                        }
                        catch (readError) {

                            console.warn(
                                "Impossible de marquer la notification comme lue",
                                readError
                            );

                        }

                    }


                    /*
                     * =================================================
                     * 2. GET CURRENT OFFER
                     * =================================================
                     */

                    let currentOffer =
                        null;


                    try {

                        currentOffer =
                            await ConversationOfferService
                                .getCurrentOffer(
                                    inquiryId
                                );


                        console.log(
                            "CURRENT OFFER",
                            currentOffer
                        );

                    }
                    catch (offerError) {

                        console.warn(
                            "Impossible de récupérer l'offre",
                            offerError
                        );

                    }


                    /*
                     * =================================================
                     * 3. ACCEPTED OFFER
                     *
                     * => AGREEMENT
                     * => FINALIZE DRAWER
                     * =================================================
                     */

                    const isAccepted =
                        normalizeEnumValue(
                            currentOffer?.status
                        ) ===
                        "ACCEPTED";


                    const agreementId =
                        currentOffer?.agreementId;


                    if (
                        isAccepted &&
                        agreementId
                    ) {

                        dispatch(
                            closeConversationDrawer()
                        );


                        dispatch(
                            closeTransactionDrawer()
                        );


                        dispatch(
                            loadAgreementStart()
                        );


                        const agreement =
                            await TransactionService
                                .getAgreement(
                                    inquiryId,
                                    agreementId
                                );


                        if (!agreement) {

                            throw new Error(
                                "Le backend n'a retourné aucun agreement"
                            );

                        }


                        const agreementWithContext = {

                            ...agreement,

                            inquiryId,

                            agreementId

                        };


                        dispatch(
                            loadAgreementSuccess(
                                agreementWithContext
                            )
                        );


                        dispatch(
                            openTransactionDrawer()
                        );


                        return;

                    }


                    /*
                     * =================================================
                     * 4. NORMAL CONVERSATION
                     * =================================================
                     */

                    dispatch(
                        loadConversationStart()
                    );


                    const conversationDetails =
                        await ConversationNotificationService
                            .getConversationDetails(
                                inquiryId
                            );


                    const {
                        conversation,
                        messages
                    } =
                        conversationDetails;


                    dispatch(
                        loadConversationSuccess({

                            notification,

                            conversation,

                            messages,

                            inquiryId,

                            offer:
                                currentOffer

                        })
                    );


                    dispatch(
                        openConversationDrawer()
                    );

                }
                catch (error) {

                    console.error(
                        "========== MARKETPLACE NOTIFICATION ERROR =========="
                    );

                    console.error(
                        error
                    );


                    dispatch(
                        loadConversationFailure(
                            error?.response?.data?.message ||
                            error?.message ||
                            "Impossible d'ouvrir la notification"
                        )
                    );

                }

            },
            [
                dispatch
            ]
        );


    /*
     * ============================================================
     * MARK AS READ
     * ============================================================
     */

    const handleMarkAsRead =
        useCallback(
            async notificationId => {

                if (!notificationId) {

                    return;

                }


                try {

                    await ConversationNotificationService
                        .markNotificationAsRead(
                            notificationId
                        );


                    dispatch(
                        markNotificationAsRead(
                            notificationId
                        )
                    );

                }
                catch (error) {

                    console.error(
                        "Unable to mark notification as read",
                        error
                    );

                }

            },
            [
                dispatch
            ]
        );


    /*
     * ============================================================
     * OPEN TRANSACTION DIRECTLY
     * ============================================================
     */

    const handleOpenTransaction =
        useCallback(
            async ({
                inquiryId,
                agreementId
            }) => {

                if (
                    !inquiryId ||
                    !agreementId
                ) {

                    console.error(
                        "Missing transaction identifiers",
                        {
                            inquiryId,
                            agreementId
                        }
                    );

                    return;

                }


                try {

                    dispatch(
                        closeConversationDrawer()
                    );


                    dispatch(
                        loadAgreementStart()
                    );


                    const agreement =
                        await TransactionService
                            .getAgreement(
                                inquiryId,
                                agreementId
                            );


                    if (!agreement) {

                        throw new Error(
                            "getAgreement() a retourné null ou undefined"
                        );

                    }


                    const agreementWithContext = {

                        ...agreement,

                        inquiryId,

                        agreementId

                    };


                    dispatch(
                        loadAgreementSuccess(
                            agreementWithContext
                        )
                    );


                    dispatch(
                        openTransactionDrawer()
                    );

                }
                catch (error) {

                    console.error(
                        "Unable to load agreement",
                        error
                    );


                    dispatch(
                        loadAgreementFailure(
                            error?.response?.data?.message ||
                            error?.message ||
                            "Impossible de charger l'accord"
                        )
                    );

                }

            },
            [
                dispatch
            ]
        );


    /*
     * ============================================================
     * OPEN AGREEMENT FROM AGREEMENT LIST
     * ============================================================
     */

    const handleOpenAgreement =
        useCallback(
            async agreement => {

                if (!agreement?.id) {

                    return;

                }


                if (!agreement?.inquiryId) {

                    console.error(
                        "Agreement sans inquiryId",
                        agreement
                    );

                    return;

                }


                try {

                    dispatch(
                        closeConversationDrawer()
                    );


                    dispatch(
                        loadAgreementStart()
                    );


                    const fullAgreement =
                        await TransactionService
                            .getAgreement(
                                agreement.inquiryId,
                                agreement.id
                            );


                    if (!fullAgreement) {

                        throw new Error(
                            "Le backend n'a retourné aucun agreement"
                        );

                    }


                    const agreementWithContext = {

                        ...fullAgreement,

                        inquiryId:
                            agreement.inquiryId,

                        agreementId:
                            agreement.id

                    };


                    dispatch(
                        loadAgreementSuccess(
                            agreementWithContext
                        )
                    );


                    dispatch(
                        openTransactionDrawer()
                    );

                }
                catch (error) {

                    console.error(
                        "Unable to load agreement",
                        error
                    );


                    dispatch(
                        loadAgreementFailure(
                            error?.response?.data?.message ||
                            error?.message ||
                            "Impossible de charger l'accord"
                        )
                    );

                }

            },
            [
                dispatch
            ]
        );

    /*
     * ============================================================
     * AGREEMENT VALIDATION
     * ============================================================
     *
     * L'acheteur valide l'accord : le drawer de validation se ferme et le
     * parcours livraison + paiement s'ouvre. Les choix (livraison, adresse,
     * paiement) sont enregistrés par ce parcours au moment de payer.
     */
    const handleAgreementValidation =
        useCallback(
            agreement => {
                dispatch(closeTransactionDrawer());
                dispatch(clearTransaction());
                setPaymentAgreement(agreement);
            },
            [
                dispatch
            ]
        );

    /*
     * ============================================================
     * PAYER MAINTENANT (accord PAYMENT_PENDING)
     * ============================================================
     */
    const handlePayAgreement =
        useCallback(
            agreement => {
                if (agreement?.id && agreement?.inquiryId) {
                    setPaymentAgreement(agreement);
                }
            },
            []
        );

    /*
     * ============================================================
     * RETOUR DE CHECKOUT HÉBERGÉ (carte / PayPal)
     * ============================================================
     *
     * Le provider redirige vers ?paymentReturn=...&agreementId=... :
     * on rouvre le drawer qui suit le statut confirmé par le serveur.
     */
    useEffect(
        () => {
            const params =
                new URLSearchParams(window.location.search);
            const returnedAgreementId =
                params.get("agreementId");

            if (!params.get("paymentReturn") || !returnedAgreementId) {
                return;
            }

            const returned =
                agreements.find(agreement => agreement.id === returnedAgreementId);

            if (!returned) {
                return;
            }

            setPaymentAgreement(returned);

            ["paymentReturn", "paymentId", "agreementId"].forEach(key => params.delete(key));
            const query = params.toString();
            window.history.replaceState(
                null,
                "",
                window.location.pathname + (query ? `?${query}` : "")
            );
        },
        [
            agreements
        ]
    );

    /*
     * ============================================================
     * NOTIFICATION COUNTS
     * ============================================================
     */

    const notificationCounts =
        useMemo(
            () => {

                const conversationNotifications =
                    notifications.filter(
                        notification =>
                            isConversationNotification(
                                notification
                            )
                    );


                const unreadNotifications =
                    notifications.filter(
                        notification =>
                            !notification.read
                    );


                const pendingAgreements =
                    agreements.filter(
                        agreement =>
                            normalizeEnumValue(
                                agreement.status
                            ) ===
                            "CREATED"
                    );


                const paymentAgreements =
                    agreements.filter(hasCheckout);


                return {

                    all:
                        notifications.length,

                    unread:
                        unreadNotifications.length,

                    negotiation:
                        conversationNotifications.length,

                    offer:
                        0,

                    agreement:
                        pendingAgreements.length,

                    payment:
                        paymentAgreements.length,

                    delivery:
                        0,

                    archived:
                        0

                };

            },
            [
                notifications,
                agreements
            ]
        );


    /*
     * ============================================================
     * RENDER TAB CONTENT
     * ============================================================
     */

    const renderTabContent =
        () => {

            switch (filter) {

                case "AGREEMENT":

                    return (

                        <AgreementList
                            agreements={
                                agreements
                            }
                            currentUserId={currentUserId}
                            onPay={handlePayAgreement}
                            onSelect={
                                handleOpenAgreement
                            }
                        />

                    );


                case "PAYMENT":

                    return (

                        <PaymentList
                            currentUserId={currentUserId}
                            onPay={handlePayAgreement}
                            agreements={
                                agreements.filter(hasCheckout)
                            }
                        />

                    );


                case "NEGOTIATION":

                    return (

                        <NegotiationList
                            notifications={
                                notifications.filter(
                                    isConversationNotification
                                )
                            }
                        />

                    );


                case "UNREAD":

                    return (

                        <NotificationList

                            loading={
                                loading
                            }

                            notifications={
                                notifications.filter(
                                    notification =>
                                        !notification.read
                                )
                            }

                            onOpenConversation={
                                openConversation
                            }

                            onOpenTransaction={
                                handleOpenTransaction
                            }

                            onMarkAsRead={
                                handleMarkAsRead
                            }

                        />

                    );


                case "ARCHIVED":

                    return (

                        <ArchivedNotificationList
                            notifications={
                                notifications
                            }
                        />

                    );


                case "OFFER":

                    return (

                        <OfferList
                            notifications={
                                notifications.filter(
                                    isConversationNotification
                                )
                            }
                        />

                    );


                case "DELIVERY":

                    return (

                        <DeliveryList
                            orders={
                                []
                            }
                        />

                    );


                case "ALL":
                default:

                    return (

                        <NotificationList

                            loading={
                                loading
                            }

                            notifications={
                                notifications
                            }

                            onOpenConversation={
                                openConversation
                            }

                            onOpenTransaction={
                                handleOpenTransaction
                            }

                            onMarkAsRead={
                                handleMarkAsRead
                            }

                        />

                    );

            }

        };


    /*
     * ============================================================
     * RENDER
     * ============================================================
     */

    return (

        <div>

            <Title level={3}>
                Centre de notifications Marketplace
            </Title>


            <NotificationStats
                notifications={
                    notifications
                }
                unreadCount={
                    unreadCount
                }
            />


            <div
                style={{
                    marginTop: 25
                }}
            >

                <NotificationFilter
                    value={
                        filter
                    }
                    onChange={
                        setFilter
                    }
                    counts={
                        notificationCounts
                    }
                />


                <Card
                    style={{
                        marginTop: 20,
                        borderRadius: 12
                    }}
                >

                    {
                        renderTabContent()
                    }

                </Card>

            </div>


            {/* ===================================================
                CONVERSATION DRAWER
            =================================================== */}

            {
                drawerOpen &&

                <ConversationDrawer
                    onOpenTransaction={
                        handleOpenTransaction
                    }
                />

            }


            {/* ===================================================
                FINALIZE PRODUCT PURCHASE DRAWER
            =================================================== */}

            {
                transactionDrawerOpen &&

                <FinalizeProductPurchaseDrawer

                    open={
                        transactionDrawerOpen
                    }

                    agreement={
                        selectedAgreement
                    }

                    currentUserId={
                        currentUserId
                    }

                    

                    onClose={
                        () => {

                            dispatch(
                                closeTransactionDrawer()
                            );


                            dispatch(
                                clearTransaction()
                            );

                        }

                    }

                    onContinue={handleAgreementValidation}

                />

            }



            <PaymentFinalizationDrawer
                open={Boolean(paymentAgreement)}
                agreement={paymentAgreement}
                currentUserId={currentUserId}
                onClose={() => {
                    setPaymentAgreement(null);
                    loadAgreements();
                }}
                onPaymentSettled={() => loadAgreements()}
            />


        </div>

    );

};


export default Notification;
